/**
 * What the yard looks like rather than what it does: draw distance and fog,
 * the light rig and the blackout, the kamiina's fire, the tent's rig, the
 * crowd's dancing and the wave at the sauna door.
 *
 * Render-only. None of it is in the fingerprint, and changing any of it must
 * leave the hash alone. See `src/config/AGENTS.md`.
 */

export const RENDER = {
  fov: 82,
  near: 0.05,
  far: 400,
  fogNear: 55,
  fogFar: 300,

  /**
   * Two drawn surfaces landing on one plane, and which of them you see.
   *
   * Render-only, like the rest of `RENDER`: `src/render/coplanar.ts` reads
   * these to decide a draw order and moves nothing, so no collider and no
   * leaderboard time depends on any of them. That file says why it works this
   * way; these are only the three numbers it needs.
   */
  coplanar: {
    /**
     * How close two planes have to be to count as the same one, in metres.
     *
     * Sits between the two scales the level is authored on. Surfaces meant to
     * be flush are flush to the millimetre or better, and the smallest gap the
     * blockout leaves on purpose is the centimetre the ground patches are
     * stacked at - which has to stay a gap.
     */
    samePlane: 0.004,
    /**
     * And how much of each other two faces have to actually cover, in metres,
     * before it matters. Two boxes standing side by side share the edge where
     * they meet, and an edge is not a surface.
     */
    overlap: 0.002,
    /**
     * How far behind the winner the loser is drawn, in depth buffer units per
     * level - not in metres. One unit is the smallest difference the buffer is
     * guaranteed to resolve at that pixel, so this is the same nudge at two
     * metres as it is at a hundred: four of them is comfortably more than the
     * rounding it has to beat, and still far too little to reorder two
     * surfaces that were a centimetre apart on purpose.
     */
    bias: 4,
  },
} as const;

/**
 * ==========================================================================
 *  THE LIGHTS, AND LOSING THEM
 * ==========================================================================
 *  Four global lights, no shadow maps: a PS2-era blockout, and a shadow map is
 *  one more thing that could differ between two machines running the same
 *  replay. `lit` is the morning the game has always looked like.
 *
 *  `blackout` is that morning with the venue's power gone - which happens when
 *  somebody runs through the bemari's charging cable, because that cable is
 *  plugged into the tolppa the juhlanavetta is fed from.
 *
 *  It is applied *indoors only*, faded in over `fade` metres past the
 *  navetta's outer walls. Outside, nothing changes and nothing should: the sun
 *  does not go out because a plug came loose, and a yard that dimmed would
 *  read as a rendering bug rather than as a consequence.
 *
 *  Dark, not black. The juhlanavetta's south wall has four windows in it, so
 *  there is daylight in the room whatever the mains is doing - and a run you
 *  cannot finish is a worse punishment than the one this is meant to be.
 *
 *  Rendering only. Nothing here is stepped by the simulation or hashed into
 *  `simulationFingerprint`, so a blackout costs no leaderboard time.
 * ==========================================================================
 */
export const LIGHTING = {
  lit: { hemisphere: 1.0, ambient: 0.75, sun: 1.05, fill: 0.35 },
  blackout: { hemisphere: 0.16, ambient: 0.08, sun: 0.1, fill: 0.06 },
  /**
   * How far past a wall the blackout takes full hold, in metres. Wide enough
   * that walking in through the porch reads as a doorway rather than as a cut,
   * narrow enough to be over before the eteinen is.
   */
  fade: 1.5,
  /** How far *outside* a wall it starts. A doorway is a threshold, not a line. */
  threshold: 0.3,
  /** What the monitors and the tolppa's charge lights read as with no power. */
  deadScreen: 0x14161a,
} as const;

/**
 * The fire in the kamiina's hatch.
 *
 * Rendering only. Nothing here is stepped by the simulation or hashed into the
 * fingerprint - what the fire *does* is decided by `KLAPI` and by whether a
 * klapi is in it, and this is only how that reads on screen.
 *
 * `embers` is the state the whole encounter exists because of: a fire that has
 * been going out since four in the morning, showing as a sliver of orange
 * under a hatch that has not shut square since it was new. `burning` is what a
 * log buys, and the flare between them is the log catching.
 */
export const FIRE = {
  /** Separate tongues across the hatch. Enough not to pulse as one block. */
  tongues: 5,
  /** How wide and how tall the fire is, at full strength, in metres. */
  width: 0.22,
  height: 0.28,
  /**
   * How far behind the hatch's own plane the tongues sit, so they read as
   * something seen *through* the plate rather than as paint on the front of it.
   */
  inset: 0.055,
  /** Strength before the klapi, and after it. */
  embers: 0.22,
  burning: 1.0,
  /** The log catching: how big, and over how long. */
  flarePeak: 0.9,
  flareDuration: 1.4,
  /** How fast the tongues move, and how solid they are at their thickest. */
  flicker: 7.5,
  opacity: 0.85,
  hot: 0xffb347,
  cool: 0xff6a1a,
} as const;

/**
 * ==========================================================================
 *  THE LIGHT SHOW IN THE XLMOTO TENT
 * ==========================================================================
 *  Rendering only, and the whole block is on the exclusion list for the same
 *  reason `FIRE` is: nothing here is stepped by the simulation, nothing here
 *  is hashed, and what the rig *does* - which is nothing at all to a runner -
 *  is decided somewhere else. See `src/config/CLAUDE.md`.
 *
 *  The one thing worth being deliberate about is that all of it is timed off
 *  wall-clock render time, exactly like the projector's clip and the chimney's
 *  smoke. A strobe that restarted its pattern because somebody pressed R would
 *  be a strobe that is really a cutscene, and a mirror ball that stopped
 *  turning on the READY screen would be a mirror ball nobody plugged in.
 *
 *  The numbers below are a rig at nine in the morning rather than a rig in a
 *  dark room, and that is deliberate too: the flashes are short and hard and
 *  the beams are wide and bright, because the thing they are competing with is
 *  a low sun on a gravel yard. See `DiscoSpec`.
 * ==========================================================================
 */
export const DISCO = {
  /** Seconds for the mirror ball to turn once. Slow: it is hung, not driven. */
  ballSpin: 11.0,
  /**
   * How subdivided the ball is: 0 is a bare icosahedron's twenty faces, 1 is
   * eighty and 2 is a hundred and eighty. A hundred and eighty facets on a
   * 30 cm ball is a couple of centimetres of mirror each, which is about what
   * one is - and at this subdivision the sphere is still visibly faceted,
   * which is the whole point of drawing it flat-shaded. Smooth it and the
   * mirror ball becomes a grey ball.
   */
  ballFacets: 2,
  /**
   * The strobe's own cycle, in seconds, and how long one flash inside it
   * lasts. The flash is deliberately far shorter than a frame at 60 Hz is
   * long - a strobe that is on for a tenth of a second is a lamp.
   */
  period: 0.5,
  flash: 0.035,
  /**
   * The second flash of the pair, as a fraction of the period after the first.
   * A real strobe fires in bursts rather than on a metronome, and two hits a
   * hair apart is what tells the eye it is a strobe rather than a bulb on a
   * timer.
   *
   * The pair has to fit inside one head's slot, though, and there are four
   * heads sharing the period: at four evenly spaced phases every head owns a
   * quarter of it, so a gap much past that puts one head's second hit on top
   * of the next head's first. `Disco.test.ts` holds the eight hits apart
   * rather than trusting this arithmetic.
   */
  doubleGap: 0.09,
  /**
   * How bright the light in the air is at the lens, 0..1, and how much of that
   * is left where the cone lands. Both are high for a light effect, because
   * this one has a sunlit yard behind it: the cone is additive, so what these
   * decide is how much it can add.
   */
  beamOpacity: 0.62,
  beamFalloff: 0.12,
  /**
   * The single point light the whole rig shares, and how far it carries. One
   * rather than one per head: four flashes inside one 3 m tent are one flash
   * from anywhere the player ever stands, and a forward renderer pays for
   * every light on every material in the level.
   */
  lightIntensity: 9.0,
  lightDistance: 16.0,
  /** Seconds for the lasers to sweep out and back once. */
  laserSweep: 6.5,
  /** How far off the middle of its fan a projector swings, radians. */
  laserSwing: 0.5,
  /** A beam's thickness in metres, and how solid it is where it leaves. */
  laserWidth: 0.028,
  laserOpacity: 0.5,
  /**
   * And how much of it is left at the far end. Far higher than `beamFalloff`,
   * because that is the whole difference between the two: a strobe's cone is
   * light spreading out and getting thinner, and a laser is a beam that
   * arrives at the other end of the yard with most of itself intact. A laser
   * that faded like a floodlight would be a floodlight.
   */
  laserFalloff: 0.45,
  /**
   * How lit a strobe's own lens is between hits, 0..1. Not zero: a head that
   * goes to black is a head that disappears, and four black boxes under a
   * canopy is not a rig.
   */
  lensIdle: 0.12,
  /**
   * The tail on the mirror ball's own glint: how much of the flash it keeps
   * between hits. A ball lit only when a strobe fires is a ball that is
   * invisible seven eighths of the time; a ball at full brightness always is a
   * white sphere. This is the part of the flash that hangs on it.
   */
  ballGlow: 0.35,
} as const;

/**
 * ==========================================================================
 *  AND WHAT THE PEOPLE UNDER THOSE LAMPS ARE DOING
 * ==========================================================================
 *  `DISCO` is the rig; this is the four who are still on their feet in front
 *  of it. They jump, and their hands come down and go back up, and both are on
 *  the record's own beat. `src/render/Dance.ts` owns the curves.
 *
 *  ------------------------------------------------------------------------
 *   RENDER-ONLY, AND THE EASIEST CASE OF IT IN THE FILE
 *  ------------------------------------------------------------------------
 *  `DISCO`'s argument for being outside the fingerprint is that nothing in the
 *  simulation has been told the tent has lamps in it. This one is a step
 *  simpler again: `LevelDefinition.standers` is not hashed at all, nobody in
 *  the tent is a collider, and a man in mid-air is a man drawn twenty
 *  centimetres higher up than he was last frame. Every number here can be
 *  re-tuned without moving a single leaderboard time.
 * ==========================================================================
 */
export const DANCE = {
  /**
   * The beat they are on, in seconds.
   *
   * 87.5 BPM, and that is not a number picked for how it looked: it is what
   * `teltta-dj.m4a` runs at, measured off the file. A crowd bouncing at the
   * speed of the record is the one thing about this anybody standing in the
   * yard can check against what they are hearing - see `Dance.ts` for why the
   * *bar* is not matched and cannot be.
   */
  beat: 60 / 87.5,
  /**
   * How high one gets, in metres, before `vigour`. About what a person on
   * grass at nine in the morning manages: enough that the feet visibly leave
   * it, nowhere near enough to be an athlete.
   */
  hop: 0.2,
  /**
   * Gravity for a jumping man. The real number rather than `PLAYER.gravity`,
   * for the same reason `SHATTER.gravity` is: this is a body drawn falling in
   * a yard, not Kimmo's hop, and Kimmo's is 22 because a platformer wants a
   * short arc rather than because the world he is in has heavy air. Nothing
   * out here is being raced.
   */
  gravity: 9.81,
  /**
   * How far the hands swing below where `raiseArms` put them, in radians.
   * Roughly a forearm's worth of travel at this distance: past that the arms
   * start folding at the shoulder rather than pumping.
   */
  pump: 0.42,
  /**
   * How far off the beat one of them can be, as a fraction of it. Small, and
   * it has to be: this is scatter rather than disagreement. Four people who
   * are each a tenth of a beat out are a crowd, and four who are half a beat
   * out are four people at four different parties.
   */
  spread: 0.13,
  /**
   * And how much harder one of them can be going at it than another, either
   * way from 1. Scales the hop and the swing together, because they are one
   * gesture - a man throwing his hands harder is a man getting further off the
   * ground.
   */
  vary: 0.22,
} as const;

/**
 * ==========================================================================
 *  AND THE ONE HAND THAT GOES UP INDOORS
 * ==========================================================================
 *  The sauna door is the one press in the game that moves nothing, and Kempf
 *  has been answering it since before anybody in the building woke up. He
 *  says his line - `KEMPF_LINE` - and raises his straight right arm forward
 *  and upward, held while he explains himself,
 *  and put away again. `src/render/Greeting.ts` owns the curve.
 *
 *  ------------------------------------------------------------------------
 *   THE ARM STAYS STRAIGHT THROUGH THE WHOLE GESTURE
 *  ------------------------------------------------------------------------
 *  The shoulder supplies the lift, with a slight turn toward his right and
 *  the door. The elbow is unbent in both the resting and raised poses, so
 *  interpolation keeps the upper arm and forearm aligned on the way up and
 *  down too. `World` reads these angles without adding a separate pose.
 *
 *  ------------------------------------------------------------------------
 *   RENDER-ONLY, AND THE SAME EASY CASE `DANCE` IS
 *  ------------------------------------------------------------------------
 *  `LevelDefinition.standers` is not hashed, the man is not a collider, and
 *  the door stays shut whatever he does with his arm. Nothing here reaches
 *  `simulationFingerprint()`, so all of it can be re-tuned on looks alone.
 * ==========================================================================
 */
export const GREETING = {
  /** Seconds the hand takes to get up there. Prompt: he saw you coming. */
  raise: 0.32,
  /**
   * And how long it stays there, which is longer than the sentence under it.
   * `KEMPF_LINE` is 4.2 s, so the hand is still up when he stops talking -
   * a man who drops his hand on his own full stop is a man dismissing you.
   */
  hold: 5,
  /** Slower down than up, because there is nothing at the other end of it. */
  drop: 0.55,
  /**
   * Where the arm ends up, in radians, read the way `raiseArms` reads its
   * own three: `lift` above horizontal-forward, `splay` round towards his own
   * side, `elbow` the fold left in it. See the header for why the weight is
   * where it is.
   */
  lift: Math.PI / 5,
  splay: 0.2,
  elbow: 0,
} as const;
